import { useCallback, useEffect, useRef, useState } from 'react';
import { toast } from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
import type { PrivateAIAssistantChatMessageJSON, UserId, ViewModelId } from 'balus-common';
import type { UIMessage } from 'ai';
import {
  PrivateAIAssistantChatMessageRepository,
  PrivateAIAssistantChatThreadRepository,
  buildPrivateAIAssistantChatMessage,
} from '@view-model/application/ai/PrivateAIAssistantChatRepository';
import { extractMessageText } from './CopyMessageButton';
import { normalizeRestoredMessage } from './normalizeRestoredMessage';
import { sanitizeAIAssistantMessagesForHistory } from './sanitizeAIAssistantMessages';

// スレッド一覧に表示するタイトルの最大長。スキーマ上限(200)より短い表示向けの長さに抑える
const THREAD_TITLE_MAX_LENGTH = 50;

type Args = {
  viewModelId: ViewModelId;
  userId: UserId;
  setMessages: (messages: UIMessage[]) => void;
  // 開くスレッドの指定。未指定の場合は最新スレッドを開く（無ければ新規 threadId を発行する）。
  // 未作成の threadId が指定された場合は空スレッドとして扱う
  requestedThreadId?: string;
};

type HistoryState = { status: 'restoring' } | { status: 'ready'; threadId: string };

type UseAIAssistantChatHistoryResult =
  | { status: 'restoring' }
  | {
      status: 'ready';
      threadId: string;
      saveMessages: (messages: UIMessage[]) => Promise<void>;
    };

/**
 * AI アシスタントのチャット履歴を RTDB に保存／復元するフック。
 *
 * - 初回マウントで requestedThreadId のスレッド（未指定なら最新スレッド）を取得して
 *   setMessages で復元する。無ければ新しい threadId を発行する。
 * - スレッド本体の永続化は最初の saveMessages まで遅延する。メッセージを送らずに閉じた
 *   場合に空スレッドが RTDB に残り、履歴一覧に表示されてしまうのを防ぐため。
 * - 保存はフックが内部で監視せず、呼び出し側が saveMessages を明示的に呼ぶ。
 *   useChat の onFinish などのライフサイクルコールバックから渡すことを想定。
 * - saveMessages は status==='ready' のときのみ型上アクセス可能。
 */
export function useAIAssistantChatHistory({
  viewModelId,
  userId,
  setMessages,
  requestedThreadId,
}: Args): UseAIAssistantChatHistoryResult {
  const { t } = useTranslation();
  const [state, setState] = useState<HistoryState>({ status: 'restoring' });

  // メッセージごとの createdAt を ID をキーに保持する。
  // saveMessages では messages を毎回 upsert するため、新規メッセージにだけ Date.now() を割り当てて
  // 既存メッセージは初出時の時刻を使い、createdAt が書き換わって並びが壊れないようにする。
  const messageCreatedAtRef = useRef<Map<string, number>>(new Map());

  // スレッドタイトルが設定済みかどうか。未設定の間だけ saveMessages でタイトルを導出・保存する
  const titleEnsuredRef = useRef(false);

  // スレッド本体が RTDB に存在することを確認済みかどうか。
  // 未確認の間は saveMessages の先頭で upsert してから保存する
  const threadEnsuredRef = useRef(false);

  // 履歴復元
  useEffect(() => {
    let disposed = false;
    setState({ status: 'restoring' });
    messageCreatedAtRef.current = new Map();
    titleEnsuredRef.current = false;
    threadEnsuredRef.current = false;

    const parseStored = (stored: PrivateAIAssistantChatMessageJSON[]): UIMessage[] => {
      const parsed: UIMessage[] = [];
      for (const message of stored) {
        try {
          parsed.push(
            normalizeRestoredMessage(JSON.parse(message.payload) as UIMessage, t('viewModel.aiChat.toolInterrupted'))
          );
          messageCreatedAtRef.current.set(message.id, message.createdAt);
        } catch (err) {
          console.error('Failed to parse stored AI assistant message payload', err);
        }
      }
      return parsed;
    };

    void (async () => {
      let restoredMessages: UIMessage[] | null = null;
      let restoredThreadId: string | null = null;
      try {
        if (requestedThreadId !== undefined) {
          const stored = await PrivateAIAssistantChatMessageRepository.getMessages(
            viewModelId,
            userId,
            requestedThreadId
          );
          if (disposed) return;
          restoredMessages = parseStored(stored);
          restoredThreadId = requestedThreadId;
          // メッセージがあるならスレッド本体も存在しているとみなす
          threadEnsuredRef.current = restoredMessages.length > 0;
        } else {
          const latest = await PrivateAIAssistantChatThreadRepository.getLatestThread(viewModelId, userId);
          if (disposed) return;
          if (latest) {
            const stored = await PrivateAIAssistantChatMessageRepository.getMessages(viewModelId, userId, latest.id);
            if (disposed) return;
            titleEnsuredRef.current = latest.title !== '';
            threadEnsuredRef.current = true;
            restoredMessages = parseStored(stored);
            restoredThreadId = latest.id;
          }
        }
      } catch (err) {
        if (disposed) return;
        console.error('Failed to restore AI assistant chat history', err);
        toast.error(t('viewModel.aiChat.toastLoadHistoryError'));
      }

      if (restoredMessages !== null && restoredThreadId !== null) {
        setMessages(restoredMessages);
        setState({ status: 'ready', threadId: restoredThreadId });
        return;
      }

      // 復元できなかった (例外 or 既存スレッドなし) ので新規 threadId を発行して ready に進める。
      // スレッド本体は最初の saveMessages で永続化する
      const newId = PrivateAIAssistantChatThreadRepository.generateThreadId(viewModelId, userId);
      if (disposed) return;
      setState({ status: 'ready', threadId: newId });
    })();
    return () => {
      disposed = true;
    };
  }, [viewModelId, userId, setMessages, t, requestedThreadId]);

  const saveMessages = useCallback(
    async (messages: UIMessage[]) => {
      if (state.status !== 'ready') return;
      if (messages.length === 0) return;

      const threadId = state.threadId;
      try {
        const now = Date.now();
        const toSave = sanitizeAIAssistantMessagesForHistory(messages).map((message, index) => {
          const existing = messageCreatedAtRef.current.get(message.id);
          const createdAt = existing ?? now + index;
          messageCreatedAtRef.current.set(message.id, createdAt);
          return buildPrivateAIAssistantChatMessage({
            id: message.id,
            role: message.role,
            payload: JSON.stringify(message),
            createdAt,
          });
        });
        // スレッド本体はここで初めて永続化する（既存スレッドには no-op）
        if (!threadEnsuredRef.current) {
          await PrivateAIAssistantChatThreadRepository.upsertEmpty(viewModelId, userId, threadId, '');
          threadEnsuredRef.current = true;
        }
        await PrivateAIAssistantChatMessageRepository.saveMessages(viewModelId, userId, threadId, toSave);
        await PrivateAIAssistantChatThreadRepository.touch(viewModelId, userId, threadId);

        // タイトル未設定のスレッドには、最初のユーザーメッセージから導出したタイトルを一度だけ保存する
        if (!titleEnsuredRef.current) {
          const firstUserMessage = messages.find((message) => message.role === 'user');
          const title = firstUserMessage
            ? extractMessageText(firstUserMessage.parts).trim().slice(0, THREAD_TITLE_MAX_LENGTH)
            : '';
          if (title !== '') {
            await PrivateAIAssistantChatThreadRepository.setTitleIfEmpty(viewModelId, userId, threadId, title);
            titleEnsuredRef.current = true;
          }
        }
      } catch (err) {
        console.error('Failed to save AI assistant chat history', err);
      }
    },
    [state, viewModelId, userId]
  );

  if (state.status === 'restoring') {
    return { status: 'restoring' };
  }
  return { status: 'ready', threadId: state.threadId, saveMessages };
}
